import { FormModal, SubmitProps } from '@/components/form-modal';
import { useToast } from '@/components/ui/hooks/use-toast';
import {
  createStudentGroupReassignmentRequest,
  getPendingStudentGroupReassignmentRequests,
  getStudentGroupReassignmentRequests,
  getStudentGroup,
  getStudentGroups,
} from '@/lib/api-calls';
import { airServerErrorFrom } from '@/lib/error-handling';
import {
  getReassignmentRequestFormDefaultValues,
  ReassignmentRequestFormValues,
  reassignmentRequestFormSchema,
} from '@/lib/student-group-reassignment-utils';
import { ReassignmentRequestForm } from '@/routes/_layout/student-groups/-components/reassignment-request-form';
import { StudentGroupWithScheduleAndLevel, studentGroupReassignmentReasonSchema } from '@airreading/common/data';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ReactNode } from 'react';

interface StudentGroupReassignmentRequestModalProps {
  studentGroup: StudentGroupWithScheduleAndLevel;
  trigger: ReactNode;
}

export const StudentGroupReassignmentRequestModal = ({
  studentGroup,
  trigger,
}: StudentGroupReassignmentRequestModalProps) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const previousTeacherName = studentGroup.coach?.fullNameOrEmail;
  const countAsReassignedLabel = studentGroup.coach
    ? previousTeacherName
      ? `Count as a reassigned student group from ${previousTeacherName}`
      : 'Count as a reassigned student group from the previous teacher'
    : null;

  const { mutateAsync } = useMutation({
    mutationFn: createStudentGroupReassignmentRequest,
  });

  const onSubmit = async ({ formData, closeModal, reset }: SubmitProps<ReassignmentRequestFormValues>) => {
    const preferredCoachId = formData.preferredNewTeacher.value.trim() || null;
    const specialRequirements = formData.specialRequirements?.trim() || null;

    try {
      await mutateAsync({
        studentGroupId: studentGroup.id,
        effectivePlainDate: formData.effectiveDate,
        requesterId: formData.requester.value,
        reasons: formData.reassignmentReasons.map(({ value }) => studentGroupReassignmentReasonSchema.parse(value)),
        preferredCoachId,
        specialRequirements,
        countAsReassignedFromTestTeacher: formData.countAsReassignedFromTestTeacher,
      });

      toast({
        title: 'Success',
        description: 'Reassignment request submitted successfully',
      });

      queryClient.invalidateQueries(getStudentGroup(studentGroup.id));
      queryClient.invalidateQueries(getStudentGroups());
      queryClient.invalidateQueries(getPendingStudentGroupReassignmentRequests());
      queryClient.invalidateQueries(getStudentGroupReassignmentRequests(studentGroup.id));

      reset();
      closeModal();
    } catch (error) {
      const airServerError = airServerErrorFrom(error);
      toast({
        title: 'Error',
        description: airServerError?.message ?? 'Failed to submit reassignment request',
        variant: 'destructive',
      });
    }
  };

  return (
    <FormModal
      modalTitle="Reassignment request"
      formSchema={reassignmentRequestFormSchema}
      defaultValues={getReassignmentRequestFormDefaultValues()}
      onSubmit={onSubmit}
      trigger={trigger}
      confirmBtnLabel="Submit"
      dialogContentProps={{ className: 'max-w-lg lg:max-w-lg max-h-none gap-3 p-4' }}
    >
      {({ form }) => <ReassignmentRequestForm form={form} countAsReassignedLabel={countAsReassignedLabel} />}
    </FormModal>
  );
};
